import moment from "moment";
import {
  ChecklistFieldDef,
  ChecklistSectionDef,
  repeatableEntriesKey,
} from "../components/DynamicChecklistManagement/DynamicChecklistManagement";
import { DynamicChecklistRuntimeValue } from "../components/DynamicChecklistRuntime/DynamicChecklistRuntime";
import {
  normalizeDynamicTagId,
  MISSING_LOD_REPEATABLE_SUFFIX,
  PRE_DISBURSEMENT_REPEATABLE_SUFFIX,
  POST_DISBURSEMENT_REPEATABLE_SUFFIX,
  normalizeMissingSheetsScalar,
  isMissingSheetsField,
} from "./dynamicChecklistScrutinyPrefill";

export const CHECKLIST_DATE_DISPLAY_FORMAT = "DD.MM.YYYY";

const CHECKLIST_DATE_PARSE_FORMATS = [
  "DD.MM.YYYY",
  "D.M.YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "DD/MM/YYYY",
  moment.ISO_8601,
];

/** Dynamic tag ids that represent a calendar date in scrutiny / checklist. */
export const CHECKLIST_DATE_TAG_IDS = new Set([
  "date",
  "dateverified",
  "dataverified",
  "predisbursementdate",
  "postdisbursementdate",
]);

export function isChecklistDateTag(tagId: string | number | undefined | null): boolean {
  const tag = normalizeDynamicTagId(tagId).toLowerCase();
  return CHECKLIST_DATE_TAG_IDS.has(tag);
}

export function isChecklistDateField(field: {
  type?: string;
  scrutinyLinkedDeedId?: string | number;
  label?: string;
}): boolean {
  if (field.type === "date") return true;
  if (isChecklistDateTag(field.scrutinyLinkedDeedId)) return true;
  const label = String(field.label ?? "")
    .trim()
    .toLowerCase();
  return label === "date" || label === "date verified";
}

export function maskChecklistDateInput(value: string | undefined | null): string {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 8);
  if (!digits.length) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

/** Value shown in the date text box (load from API + live typing). */
export function checklistDateInputValue(value: string | undefined | null): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const iso = moment(raw, "YYYY-MM-DD", true);
  if (iso.isValid()) return iso.format(CHECKLIST_DATE_DISPLAY_FORMAT);
  const dotted = moment(raw, CHECKLIST_DATE_DISPLAY_FORMAT, true);
  if (dotted.isValid()) return dotted.format(CHECKLIST_DATE_DISPLAY_FORMAT);
  return maskChecklistDateInput(raw);
}

export function formatChecklistDateForDisplay(value: string | undefined | null): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const iso = moment(raw, "YYYY-MM-DD", true);
  if (iso.isValid()) return iso.format(CHECKLIST_DATE_DISPLAY_FORMAT);
  const dotted = moment(raw, CHECKLIST_DATE_DISPLAY_FORMAT, true);
  if (dotted.isValid()) return dotted.format(CHECKLIST_DATE_DISPLAY_FORMAT);
  const parsed = moment(raw, ["DD-MM-YYYY", "DD/MM/YYYY"], true);
  if (parsed.isValid()) return parsed.format(CHECKLIST_DATE_DISPLAY_FORMAT);
  return maskChecklistDateInput(raw);
}

/** Convert user/display input to YYYY-MM-DD for scrutiny and database writes. */
export function normalizeChecklistDateForStorage(value: string | undefined | null): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const dotted = moment(raw, "DD.MM.YYYY", true);
  if (dotted.isValid()) return dotted.format("YYYY-MM-DD");
  const iso = moment(raw, "YYYY-MM-DD", true);
  if (iso.isValid()) return iso.format("YYYY-MM-DD");
  const parsed = moment(raw, CHECKLIST_DATE_PARSE_FORMATS, true);
  if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  return raw;
}

export function checklistValueForMappedSave(
  field: ChecklistFieldDef,
  value: string | undefined | null
): string {
  const strVal = value == null ? "" : String(value);
  if (!strVal.trim()) return "";
  if (isMissingSheetsField(field)) {
    return normalizeMissingSheetsScalar(strVal);
  }
  return isChecklistDateField(field)
    ? normalizeChecklistDateForStorage(strVal)
    : strVal;
}

function formatMissingSheetsCell(field: ChecklistFieldDef, target: Record<string, string>): void {
  if (!isMissingSheetsField(field)) return;
  const current = target[field.id];
  if (current == null || String(current).trim() === "") return;
  target[field.id] = normalizeMissingSheetsScalar(current);
};

export function formatChecklistDatesInRuntime(
  sections: ChecklistSectionDef[],
  runtime: DynamicChecklistRuntimeValue
): void {
  const formatCell = (field: ChecklistFieldDef, target: Record<string, string>) => {
    if (isChecklistDateField(field)) {
      const current = target[field.id];
      if (current == null || String(current).trim() === "") return;
      target[field.id] = formatChecklistDateForDisplay(current);
      return;
    }
    formatMissingSheetsCell(field, target);
  };

  sections.forEach((section) => {
    section.fields.forEach((field) => formatCell(field, runtime.checklistValues));
    (section.repeatableGroups || []).forEach((group) => {
      const key = repeatableEntriesKey(section.id, group.id);
      const rows = runtime.repeatableValues[key] || [];
      rows.forEach((row) => {
        group.fields.forEach((field) => formatCell(field, row));
      });
    });
    [MISSING_LOD_REPEATABLE_SUFFIX, PRE_DISBURSEMENT_REPEATABLE_SUFFIX, POST_DISBURSEMENT_REPEATABLE_SUFFIX].forEach(
      (suffix) => {
        const key = repeatableEntriesKey(section.id, suffix);
        (runtime.repeatableValues[key] || []).forEach((row) => {
          section.fields.forEach((field) => formatCell(field, row));
        });
      }
    );
  });
}
